import type { VesselFileSettings } from '@felix/domain';
import type {
  DistributionView,
  DocumentPermissions,
  DocumentPrinter,
  DocumentSource,
  FolderTreeImpact,
  FolderTreeVesselResult,
  FolderTreeView,
  LibraryDetail,
  LibraryRevision,
  LibrarySection,
  LibraryTree,
  OfficeDocumentActions,
  OfficeFolderActions,
  OfficeVesselLibrary,
  UsbExportList,
  UsbExportProgress,
} from '@felix/ui';
import { ApiError, api } from './api.js';
import { tusUpload } from './tus-upload.js';

/** Doküman revizyonu yüklemesinin amacı: sunucu 1 GiB sınırını buna göre koyar (ADR 0028 EK). */
const DOCUMENT_UPLOAD_PURPOSE = 'document_revision';
const id = encodeURIComponent;

/**
 * Ofiste kütüphane yönetimi ve onay akışı (plan M8a Task 17; `apps/server/src/office/documents.ts`).
 * Dosya tus ile yüklenir; kesilirse aynı dosya yeniden seçilince kaldığı yerden sürer.
 */
export function officeDocumentActions(owner?: string): OfficeDocumentActions {
  return {
    vessels: async () =>
      (await api<{ id: string; name: string }[]>('/office/vessels')).map((v) => ({
        id: v.id,
        name: v.name,
      })),
    createDocument: (input) =>
      api<LibraryDetail>('/office/documents', { method: 'POST', body: input }),
    chainRoles: () => api<{ id: string; name: string }[]>('/office/documents/chain-roles'),
    updateDocument: (documentId, rowVersion, patch) =>
      api<LibraryDetail>(`/office/documents/${id(documentId)}`, {
        method: 'PATCH',
        body: { rowVersion, ...patch },
      }),
    // "Remove document" (ADR 0142 §5)
    removeDocument: async (documentId, rowVersion) => {
      await api<unknown>(`/office/documents/${id(documentId)}?rowVersion=${rowVersion}`, {
        method: 'DELETE',
      });
    },
    upload: (file, options) =>
      tusUpload(file, {
        purpose: DOCUMENT_UPLOAD_PURPOSE,
        ...(owner ? { owner } : {}),
        onProgress: options.onProgress,
        ...(options.signal ? { signal: options.signal } : {}),
      }),
    createRevision: (documentId, input) =>
      api<LibraryRevision>(`/office/documents/${id(documentId)}/revisions`, {
        method: 'POST',
        body: input,
      }),
    addAttachment: async (revisionId, input) => {
      await api(`/office/document-revisions/${id(revisionId)}/attachments`, {
        method: 'POST',
        body: input,
      });
    },
    act: (revisionId, action, input) =>
      api<LibraryRevision>(`/office/document-revisions/${id(revisionId)}/${action}`, {
        method: 'POST',
        body: input,
      }),
    distribution: (section) =>
      api<DistributionView>(`/office/documents/distribution?section=${id(section)}`),
    fileSettings: (vesselId) =>
      api<{ rowVersion: number | null; settings: VesselFileSettings }>(
        `/office/vessels/${id(vesselId)}/file-settings`,
      ),
    saveFileSettings: (vesselId, rowVersion, settings) =>
      api<{ rowVersion: number | null; settings: VesselFileSettings }>(
        `/office/vessels/${id(vesselId)}/file-settings`,
        {
          method: 'PUT',
          body: { ...settings, ...(rowVersion === null ? {} : { rowVersion }) },
        },
      ),
    exportList: (vesselId) =>
      api<UsbExportList>(`/office/vessels/${id(vesselId)}/document-files/export`),
    ...(directoryPicker() ? { exportToFolder } : {}),
    folders: officeFolderActions(),
    vesselLibrary: officeVesselLibrary(),
  };
}

/** Gemi kütüphanesinin dosyası: doğrulanmamışsa sunucu 409 `FILE_NOT_UPLOADED` verir. */
function vesselFileUrl(vesselId: string, fileId: string): string {
  return `/api/office/vessels/${id(vesselId)}/library/files/${id(fileId)}`;
}

/**
 * Ofiste bir geminin kütüphanesi, salt okunur (plan M8a4 Task 33; ADR 0038 §1, §12, §14;
 * `apps/server/src/office/vessel-library.ts`): ağaç gemideki gibi, taslaklar dahil; dosyalar
 * buluttaki durumlarıyla. İndirme yalnız doğrulanmış dosyada çizilir.
 */
export function officeVesselLibrary(): OfficeVesselLibrary {
  return {
    tree: (vesselId, section) =>
      api<LibraryTree>(`/office/vessels/${id(vesselId)}/library/tree?section=${id(section)}`),
    detail: (vesselId, documentId) =>
      api<LibraryDetail>(`/office/vessels/${id(vesselId)}/library/documents/${id(documentId)}`),
    fileUrl: vesselFileUrl,
    download: async (vesselId, { fileId, name }) =>
      browserDownload(vesselFileUrl(vesselId, fileId), name),
  };
}

/**
 * Klasör ağacı yönetimi (ADR 0038 §8; `apps/server/src/office/doc-trees.ts`): geminin ağacı ya da
 * şirket aynası, emekliye ayırma etkisi ve gemi seçicili işlem. 422 `TREE_OPERATION_REJECTED`
 * gemi başına hatayla döner; hiçbir gemi değişmemiştir.
 */
export function officeFolderActions(): OfficeFolderActions {
  return {
    tree: (target, section) =>
      api<FolderTreeView>(
        `/office/doc-trees?${
          'vesselId' in target ? `vesselId=${id(target.vesselId)}` : 'library=company'
        }&section=${id(section)}`,
      ),
    impact: async (section, key) =>
      (
        await api<{ vessels: FolderTreeImpact[] }>(
          `/office/doc-trees/impact?section=${id(section)}&key=${id(key)}`,
        )
      ).vessels,
    apply: async (request) => {
      try {
        const result = await api<{ vessels: FolderTreeVesselResult[] }>(
          '/office/doc-trees/operations',
          { method: 'POST', body: request },
        );
        return { ok: true, vessels: result.vessels };
      } catch (error) {
        if (!(error instanceof ApiError) || error.code !== 'TREE_OPERATION_REJECTED') throw error;
        const rejected =
          (error.payload as { vessels?: { vesselId: string; name: string | null; code: string }[] })
            ?.vessels ?? [];
        return {
          ok: false,
          vessels: rejected.map((v) => ({
            vesselId: v.vesselId,
            name: v.name,
            status: 'error' as const,
            code: v.code,
          })),
        };
      }
    },
  };
}

// --- "Export for USB" (ADR 0028 #33 b): File System Access API ---

interface WritableFile {
  createWritable(): Promise<WritableStream<Uint8Array>>;
  getFile(): Promise<File>;
}
interface DirectoryHandle {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<DirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<WritableFile>;
}
type DirectoryPicker = (options?: { mode?: 'readwrite' }) => Promise<DirectoryHandle>;

/** Chrome ve Edge'de `showDirectoryPicker`; diğer tarayıcılarda yok (düğme açıklamayla pasif). */
function directoryPicker(): DirectoryPicker | null {
  const picker = (globalThis as { showDirectoryPicker?: DirectoryPicker }).showDirectoryPicker;
  return typeof picker === 'function' ? picker.bind(globalThis) : null;
}

/**
 * Dosyalar ofis indirme ucundan tek tek, akışla alınır ve `objects/<sha[0..2]>/<sha>` düzeninde
 * yazılır; belleğe okunmaz, ZIP yok. Aynı boyutta zaten yazılmış dosya atlanır (yarıda kalan
 * dışa aktarma sürer). `manifest.json` yalnız bilgi amaçlıdır: gemi hash'e bakar.
 */
async function exportToFolder(
  list: UsbExportList,
  onProgress: (progress: UsbExportProgress) => void,
): Promise<void> {
  const picker = directoryPicker();
  if (!picker) throw Object.assign(new Error('UNSUPPORTED'), { code: 'UNSUPPORTED' });
  let root: DirectoryHandle;
  try {
    root = await picker({ mode: 'readwrite' });
  } catch {
    throw Object.assign(new Error('CANCELLED'), { code: 'CANCELLED' });
  }
  const files = [...new Map(list.files.map((f) => [f.sha256, f])).values()];
  const objects = await root.getDirectoryHandle('objects', { create: true });
  let bytes = 0;
  onProgress({ done: 0, total: files.length, bytes });
  for (const [index, file] of files.entries()) {
    const folder = await objects.getDirectoryHandle(file.sha256.slice(0, 2), { create: true });
    const handle = await folder.getFileHandle(file.sha256, { create: true });
    if ((await handle.getFile()).size !== file.size) {
      const response = await fetch(officeFileUrl(file.fileId), { credentials: 'same-origin' });
      if (!response.ok || !response.body) {
        throw Object.assign(new Error('NETWORK'), { code: 'NETWORK' });
      }
      await response.body.pipeTo(await handle.createWritable());
    }
    bytes += file.size;
    onProgress({ done: index + 1, total: files.length, bytes });
  }
  const manifest = await root.getFileHandle('manifest.json', { create: true });
  const writer = (await manifest.createWritable()).getWriter();
  await writer.write(new TextEncoder().encode(JSON.stringify(list, null, 2)));
  await writer.close();
}

/** Ofis dosya uç noktası: aynı köken, oturum çerezi, `Range` destekli (ADR 0027 §2). */
export function officeFileUrl(fileId: string): string {
  return `/api/office/document-files/${encodeURIComponent(fileId)}`;
}

/**
 * Tarayıcı indirmesi: sunucu `Content-Disposition: attachment` ve durum etiketli adla verir
 * (ADR 0028 #30); dosya belleğe okunmaz. Bağlantı geçicidir.
 */
function browserDownload(href: string, name: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = name;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
}

/**
 * Ofiste doküman kütüphanesi (plan M8a Task 14; ADR 0027): ofis API'si. Önizleyici dosyayı
 * `Range` ile okur; yazdırma tarayıcının yazdırma penceresidir (Office gerekmez [sorular #20]).
 * Basılı kopya altlığında ofis saati UTC'dir (ADR 0028 #30 A).
 */
export function officeDocumentSource(printer: DocumentPrinter, owner?: string): DocumentSource {
  return {
    scope: 'web',
    permissions: () => api<DocumentPermissions>('/office/documents/permissions'),
    tree: (section: LibrarySection) =>
      api<LibraryTree>(`/office/documents/tree?section=${encodeURIComponent(section)}`),
    detail: (id: string) => api<LibraryDetail>(`/office/documents/${encodeURIComponent(id)}`),
    fileUrl: (fileId) => officeFileUrl(fileId),
    downloadFile: async ({ fileId, name }) => browserDownload(officeFileUrl(fileId), name),
    setFavorite: async (documentId, favorite) => {
      await api(`/office/document-favorites/${encodeURIComponent(documentId)}`, {
        method: favorite ? 'PUT' : 'DELETE',
      });
    },
    print: async () => {
      window.print();
    },
    printer,
    office: officeDocumentActions(owner),
  };
}
